// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DnaSyngoviaDataConnections } from '../../interfaces/dna-syngovia-data-connections';
import { DnaSyngoviaGroupNode } from '../../interfaces/dna-syngovia-group-node';
import { DnaSyngoviaNodeType } from '../../interfaces/dna-syngovia-node';
import { DnaSyngoviaGroupNodeSelectionModel } from '../../model/dna-syngovia-group-node-selection-model';
import { DnaSyngoviaSelectionType } from '../../model/dna-syngovia-selection-model';
import { DnaSyngoviaGroupNodeService } from '../../services/dna-syngovia-group-node.service';
import { DnaSyngoviaService } from '../../services/dna-syngovia.service';
import {
  DnaSyngoviaComparableCardNodeComponent,
  SelectedNodeChangeEvent,
} from '../dna-syngovia-comparable-card-node/dna-syngovia-comparable-card-node.component';

@Component({
  standalone: true,
  imports: [CommonModule, DnaSyngoviaComparableCardNodeComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-comparison-board',
  templateUrl: './dna-syngovia-comparison-board.component.html',
  styleUrls: ['./dna-syngovia-comparison-board.component.scss'],
})
export class DnaSyngoviaComparisonBoardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('viewRef', { read: ViewContainerRef }) viewRef!: ViewContainerRef;

  @Input() groupNode: DnaSyngoviaGroupNode | undefined;
  @Input() selectedNodes: DnaSyngoviaNodeType[] | undefined;

  protected currentGroupNodeSelectionModel:
    | DnaSyngoviaGroupNodeSelectionModel
    | undefined;

  private readonly unsubscribe = new Subject<void>();

  private componentRefs: ComponentRef<DnaSyngoviaComparableCardNodeComponent>[] =
    [];

  protected readonly groupNodeSelectionModelByType =
    this.dnaSyngoviaGroupNodeService.newGroupNodeSelectionModelByType();

  public constructor(
    private readonly dnaSyngoviaService: DnaSyngoviaService,
    private readonly dnaSyngoviaGroupNodeService: DnaSyngoviaGroupNodeService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.initGroupNodesByType();
  }

  public ngAfterViewInit(): void {
    this.createComponents();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private createComponents(): void {
    if (!this.selectedNodes || this.selectedNodes.length === 0) {
      this.createComponentFn();
    } else {
      this.selectedNodes.forEach((selectedNode) => {
        const currentNodeSelection =
          this.dnaSyngoviaGroupNodeService.currentNodeSelection(
            this.currentGroupNodeSelectionModel,
            selectedNode
          );

        this.createComponentFn(
          false,
          this.currentGroupNodeSelectionModel,
          currentNodeSelection
        );
      });
    }
    this.createComponentFn(true);
    this.changeDetectorRef.detectChanges();
  }

  private initGroupNodesByType(): void {
    this.dnaSyngoviaService.getNetworkConnections.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.initGroupNodesByTypeSubscribeFn);
  }

  private readonly initGroupNodesByTypeSubscribeFn = (
    dataConnections: unknown
  ) => {
    this.dnaSyngoviaGroupNodeService.filterGroupNodes(
      this.groupNodeSelectionModelByType,
      dataConnections as DnaSyngoviaDataConnections
    );

    this.currentGroupNodeSelectionModel =
      this.dnaSyngoviaGroupNodeService.currentGroupNodeSelectionModel(
        this.groupNodeSelectionModelByType,
        this.groupNode
      );

    this.dnaSyngoviaGroupNodeService.updateGroupNodeSelectionModel(
      this.currentGroupNodeSelectionModel,
      this.selectedNodes
    );
  };

  public onSelectedNodeChange = (event: SelectedNodeChangeEvent): void => {
    if (!event.previousValue) {
      this.createComponentFn(true);
    }
  };

  private readonly createComponentFn = (
    registerEvent = false,
    groupNodeSelectionModel?: DnaSyngoviaGroupNodeSelectionModel,
    nodeSelection?: DnaSyngoviaSelectionType
  ) => {
    const componentRef = this.viewRef.createComponent(
      DnaSyngoviaComparableCardNodeComponent
    );
    this.componentRefs.push(componentRef);

    componentRef.instance.groupNodeSelectionModelByType =
      this.groupNodeSelectionModelByType;
    componentRef.instance.currentGroupNodeSelectionModel =
      groupNodeSelectionModel;
    componentRef.instance.currentNodeSelection = nodeSelection;
    componentRef.instance.removeNodeChange
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.onRemoveNodeChange);

    if (registerEvent) {
      componentRef.instance.selectedNodeChange
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(this.onSelectedNodeChange);
    }
  };

  private readonly onRemoveNodeChange = (
    componentInstance: DnaSyngoviaComparableCardNodeComponent
  ) => {
    const index = this.componentRefs.findIndex(
      (componentRef) => componentRef.instance === componentInstance
    );

    this.viewRef.remove(index);
    this.componentRefs.splice(index, 1);
    if (this.componentRefs.length == 1) {
      this.clearAllNodes();
    }
  };

  public clearAllNodes(): void {
    this.viewRef.clear();
    this.componentRefs = [];
    this.createComponentFn();
    this.createComponentFn(true);

    this.dnaSyngoviaGroupNodeService.deselectAllModelNodes(
      this.groupNodeSelectionModelByType
    );
  }
}
